/** @copyright (c) Viewpost. All Rights Reserved. See LICENSE for more details. */

import React, { useMemo, useRef } from 'react';
import { Menu, MainButton, ChildButton } from '@Viewpost/react-mfb';
import 'style-loader!css-loader!@Viewpost/react-mfb/mfb.css';

import PathLink from 'components/Link/PathLink';

import Paths from 'config/paths';

import { useLaunchCreateEditAddressBookModal } from 'containers/Modals/CreateEditAddressBook/Async';

import useFormatMessage from 'hooks/intl/useFormatMessage';
import usePathname from 'hooks/location/usePathname';

import useLaunchCreateSwitchCompanyModal from 'views/Settings/SwitchCompanies/CreateSwitchCompanyModal';

import buildingIcon from './building.svg?url';
import plusIcon from './plus.svg?url';
import crossIcon from './cross.svg?url';
import pencilIcon from './pencil.svg?url';
import dollarSignIcon from './dollarSign.svg?url';
import Messages from './index.messages';
import connect from './index.connect';
import './index.scss';

const FloatingButtonLink = (
  label,
  path,
  onClick
) => ({
  children,
  className
}) => (
  <PathLink
    data-mfb-label={label}
    path={path}
    className={className}
  >
    {children}
  </PathLink>
);

const FloatingButtonAction = (
  label,
  onClick
) => ({
  children,
  className
}) => (
  <div
    data-mfb-label={label}
    className={className}
    onClick={onClick}
  >
    {children}
  </div>
);

const ActionButtonConfig = [{
  matchOn: Paths.Bills,
  additionalActions: ['CreateBill']
}, {
  matchOn: Paths.Settings.SwitchCompanies,
  additionalActions: ['CreateSwitchCompany']
}];

const AppFloatingActionButton = ({
  canCreateAddressBook,
  canCreateBill,
  canCreateInvoice,
  canSendPayment
}) => {
  // Hooks
  const formatMessage = useFormatMessage();
  const pathname = usePathname();

  const toggleMenuRef = useRef();

  const onChildButtonClick = useMemo(
    () => evt => toggleMenuRef.current?.toggleMenu?.(evt)
  );

  const launchCreateSwitchCompanyModal = useLaunchCreateSwitchCompanyModal();
  const launchCreateEditAddressBookModal = useLaunchCreateEditAddressBookModal();

  // Render
  if (!canCreateInvoice && !canSendPayment) return null;

  let match = ActionButtonConfig.find(
    ({ matchOn }) => matchOn.match(pathname, { alsoMatchChildren: true })
  );
  let additionalActions = [];

  if (match) {
    if (match.hide) return null;

    additionalActions = match.additionalActions || [];
  }

  let actions = [];

  let showCreateBill = canCreateBill && additionalActions.includes('CreateBill');

  if (showCreateBill) {
    actions = actions.concat(
      <ChildButton
        key="create_invoice"
        className="fab-invoice-button"
        linkComponent={FloatingButtonLink(
          formatMessage(Messages.CreateBill),
          Paths.Bills.Create,
          onChildButtonClick
        )}
        iconComponent={({className}) => <img className={`fab-icon ${className}`} src={pencilIcon} />}
      />
    );
  }

  if (!showCreateBill && canCreateInvoice) {
    actions = actions.concat(
      <ChildButton
        key="create_invoice"
        className="fab-invoice-button"
        linkComponent={FloatingButtonLink(
          formatMessage(Messages.CreateInvoice),
          Paths.Invoices.Create,
          onChildButtonClick
        )}
        iconComponent={({className}) => <img className={`fab-icon ${className}`} src={pencilIcon} />}
      />
    );
  }

  if (canSendPayment) {
    actions = actions.concat(
      <ChildButton
        key="send_payment"
        className="fab-payment-button"
        linkComponent={FloatingButtonLink(
          formatMessage(Messages.SendPayment),
          Paths.Payments.SendPayment,
          onChildButtonClick
        )}
        iconComponent={({className}) => <img className={`fab-icon ${className}`} src={dollarSignIcon} />}
      />
    );
  }

  if (canCreateAddressBook) {
    actions = actions.concat(
      <ChildButton
        key="create_address_book"
        className="fab-create-address-book-button"
        linkComponent={FloatingButtonAction(
          formatMessage(Messages.AddCompany),
          (e) => {
            onChildButtonClick(e);
            launchCreateEditAddressBookModal();
          }
        )}
        iconComponent={({ className }) => (
          <img className={`fab-icon ${className}`} src={buildingIcon} />
        )}
      />
    );
  }

  // Completely usurp FAB if on Switch Company Page
  if (additionalActions.includes('CreateSwitchCompany')) {
    actions = [(
      <ChildButton
        key="create_address_book"
        className="fab-create-address-book-button"
        linkComponent={FloatingButtonAction(
          formatMessage(Messages.CreateSwitchCompany),
          (e) => {
            onChildButtonClick(e);
            launchCreateSwitchCompanyModal();
          }
        )}
        iconComponent={({ className }) => (
          <img className={`fab-icon ${className}`} src={buildingIcon} />
        )}
      />
    )];
  }

  return (
    <Menu
      effect="slidein"
      method="click"
      position="br"
      ref={toggleMenuRef}
      preventDefault={false}
    >
      <MainButton
        className="fab-main-button"
        noHref={true}
        restingIcon={({className}) => <img className={`fab-icon ${className}`} src={plusIcon} />}
        activeIcon={({className}) => <img className={`fab-icon ${className}`} src={crossIcon} />}
      />
      {actions}
    </Menu>
  );
};

export default connect(AppFloatingActionButton);
